// src/hrms/alarms/alarmActions.ts
import todoApi from "../api/todo";
import { tasksApi } from "../api/tasks";
import { addAlarmFollowUp, closeAlarm, markOccurrenceHandled } from "./alarmModel";
import { closeAlarmNotifications } from "./alarmNotifications";
import { loadAlarms, updateAlarm } from "./alarmStore";
import { itemIdOf, type AlarmItemKey, type ItemAlarm } from "./alarmTypes";

/**
 * What the alarm's answers do, shared by the in-page alarm dialog, the
 * notification buttons (routed back by the service worker) and the missed
 * list. Each one closes the occurrence's notifications, then records the
 * decision; every tab's engine picks the change up from storage.
 */

/** Fired after an alarm answer changed an item on the server, so an open planner or task page reloads it. */
export const ALARM_ITEM_CHANGED_EVENT = "teamlio:alarm-item-changed";

export type CompletionResult =
  | { kind: "completed" }
  /** A task with a reviewer: the preparer's "done" hands it over for review. */
  | { kind: "sent-for-review" }
  /** No connection: closed in this browser, sent to the server once it is back online. */
  | { kind: "queued" }
  /** The server refused; the alarm stays on. */
  | { kind: "refused"; message: string };

function hasResponse(error: unknown) {
  return Boolean((error as { response?: unknown } | null)?.response);
}

function refusalMessage(error: unknown) {
  const detail = (error as { response?: { data?: { detail?: unknown } } } | null)?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return "This item couldn't be completed from the alarm. Open it to finish it.";
}

async function sendCompletion(itemKey: AlarmItemKey): Promise<"completed" | "sent-for-review"> {
  const id = itemIdOf(itemKey);
  if (itemKey.startsWith("todo:")) {
    await todoApi.update(id, { status: "COMPLETED" });
    return "completed";
  }
  try {
    await tasksApi.changeStatus(id, { status: "DONE" });
    return "completed";
  } catch (error) {
    // A preparer whose task has a reviewer cannot close it; their "done" is
    // handing it over for review, the same step the planner's button takes.
    if (!hasResponse(error)) throw error;
    await tasksApi.sendForReview(id);
    return "sent-for-review";
  }
}

function announceItemChanged(itemKey: AlarmItemKey) {
  window.dispatchEvent(new CustomEvent(ALARM_ITEM_CHANGED_EVENT, { detail: { itemKey } }));
}

function settle(ownerId: string, itemKey: AlarmItemKey, occurrenceId: string | null, update: (alarm: ItemAlarm) => ItemAlarm) {
  if (occurrenceId) void closeAlarmNotifications(itemKey, occurrenceId);
  updateAlarm(ownerId, itemKey, (current) => (current ? update(current) : current));
}

/** Complete: stops every alarm of the item and completes it on the server. */
export async function completeFromAlarm(ownerId: string, itemKey: AlarmItemKey, occurrenceId: string | null): Promise<CompletionResult> {
  const now = Date.now();
  settle(ownerId, itemKey, occurrenceId, (alarm) => {
    const handled = occurrenceId ? markOccurrenceHandled(alarm, occurrenceId, "completed", now) : alarm;
    return closeAlarm(handled, "completed", true);
  });
  let outcome: "completed" | "sent-for-review";
  try {
    outcome = await sendCompletion(itemKey);
  } catch (error) {
    if (!hasResponse(error)) return { kind: "queued" };
    // The server said no: the item is still open, so its alarm must stay on.
    // The occurrence the user answered stays answered (they saw it).
    updateAlarm(ownerId, itemKey, (current) => (current ? { ...current, closed: undefined, pendingCompletion: false } : current));
    return { kind: "refused", message: refusalMessage(error) };
  }
  updateAlarm(ownerId, itemKey, (current) => (current ? { ...current, pendingCompletion: false } : current));
  announceItemChanged(itemKey);
  return { kind: outcome };
}

/** Snooze: silence now, ring again after the alarm's snooze length. */
export function snoozeAlarm(ownerId: string, itemKey: AlarmItemKey, occurrenceId: string, minutes?: number) {
  const now = Date.now();
  settle(ownerId, itemKey, occurrenceId, (alarm) => (
    addAlarmFollowUp(alarm, occurrenceId, "snooze", now + (minutes ?? alarm.snoozeMinutes) * 60_000, now)
  ));
}

/** Remind me again: book another alert at a chosen time; works on a missed alarm too. */
export function remindAgain(ownerId: string, itemKey: AlarmItemKey, occurrenceId: string | null, at: number) {
  const now = Date.now();
  settle(ownerId, itemKey, occurrenceId, (alarm) => addAlarmFollowUp(alarm, occurrenceId, "remind", at, now));
}

/** Dismiss: stops this alarm only. The item stays open and its later alerts still ring. */
export function dismissAlarm(ownerId: string, itemKey: AlarmItemKey, occurrenceId: string) {
  const now = Date.now();
  settle(ownerId, itemKey, occurrenceId, (alarm) => markOccurrenceHandled(alarm, occurrenceId, "dismissed", now));
}

/** Open counts as an answer: the user is looking at the item now. */
export function acknowledgeOpened(ownerId: string, itemKey: AlarmItemKey, occurrenceId: string) {
  const now = Date.now();
  settle(ownerId, itemKey, occurrenceId, (alarm) => markOccurrenceHandled(alarm, occurrenceId, "opened", now));
}

/** Where Open takes the user. */
export function alarmItemPath(itemKey: AlarmItemKey) {
  return itemKey.startsWith("todo:") ? "/todo" : `/tasks/${encodeURIComponent(itemIdOf(itemKey))}`;
}

/**
 * Completions pressed offline. A server refusal re-opens the alarm, exactly
 * as it would have at the moment the button was pressed.
 */
export async function retryPendingCompletions(ownerId: string) {
  const pending = Object.entries(loadAlarms(ownerId)).filter(([, alarm]) => alarm.pendingCompletion);
  for (const [key] of pending) {
    const itemKey = key as AlarmItemKey;
    try {
      await sendCompletion(itemKey);
      updateAlarm(ownerId, itemKey, (current) => (current ? { ...current, pendingCompletion: false } : current));
      announceItemChanged(itemKey);
    } catch (error) {
      if (!hasResponse(error)) return;
      updateAlarm(ownerId, itemKey, (current) => (current ? { ...current, closed: undefined, pendingCompletion: false } : current));
    }
  }
}
